import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, X, ArrowLeft, Target, Calendar, Users, Zap, Video } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';

const GOAL_TYPES = {
  ENGAGEMENT: {
    icon: <Users className="w-5 h-5" />,
    label: 'Engagement Rate',
    description: 'Target engagement rate for your content',
    metrics: ['likes', 'comments', 'shares'],
    defaultValue: 5, // 5%
    unit: '%'
  },
  FREQUENCY: {
    icon: <Calendar className="w-5 h-5" />,
    label: 'Post Frequency',
    description: 'How often you want to post',
    metrics: ['posts per week'],
    defaultValue: 3,
    unit: 'posts/week'
  },
  REACH: {
    icon: <Target className="w-5 h-5" />,
    label: 'Reach Goal',
    description: 'Target number of unique viewers',
    metrics: ['views', 'unique viewers'],
    defaultValue: 10000,
    unit: 'viewers'
  }
};

const VideoSlot = ({ onClick, selected, video }) => {
  return (
    <div 
      onClick={onClick}
      className={cn(
        "relative w-full pb-[177.78%]",
        "rounded-2xl border-2 border-dashed",
        "transition-all duration-200 group",
        "hover:border-indigo-500 hover:bg-indigo-50/50",
        selected ? "border-indigo-500 bg-indigo-50/30" : "border-gray-300"
      )}
    >
      <div className="absolute inset-0 flex items-center justify-center">
        {video ? (
          <div className="relative w-full h-full">
            <img 
              src={video.thumbnail} 
              alt={video.title}
              className="w-full h-full object-cover rounded-2xl"
            />
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
              <Button variant="ghost" className="text-white">
                <X className="w-5 h-5 mr-2" />
                Remove Video
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-500 group-hover:text-indigo-600">
            <Plus className="w-8 h-8" />
            <span className="text-sm">Add Video</span>
          </div>
        )}
      </div>
    </div>
  );
};

const GoalSetting = ({ type, value, onChange }) => {
  const goalType = GOAL_TYPES[type];
  
  return (
    <Card className="p-4">
      <div className="flex items-start gap-4">
        <div className="p-3 rounded-xl bg-indigo-50">
          {goalType.icon}
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-gray-900">{goalType.label}</h3>
          <p className="text-sm text-gray-500 mt-1">{goalType.description}</p>
          <div className="mt-4 flex items-center gap-3">
            <input
              type="number"
              value={value}
              onChange={(e) => onChange(Number(e.target.value))}
              className="w-24 px-3 py-1.5 border rounded-md"
            />
            <span className="text-sm text-gray-600">{goalType.unit}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

const AutomationDetail = ({ automation, onClose }) => {
  const [goals, setGoals] = useState({
    engagement: GOAL_TYPES.ENGAGEMENT.defaultValue,
    frequency: GOAL_TYPES.FREQUENCY.defaultValue,
    reach: GOAL_TYPES.REACH.defaultValue
  });

  const [videoSlots, setVideoSlots] = useState([]);

  const handleGoalChange = (type, value) => {
    setGoals(prev => ({
      ...prev,
      [type]: value
    }));

    // Calculate required video slots based on frequency
    if (type === 'frequency') {
      setVideoSlots(Array(value).fill(null));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="h-full overflow-y-auto"
    >
      {/* Header */}
      <div className="sticky top-0 bg-white/80 backdrop-blur-md border-b z-10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold">{automation.name}</h1>
              <p className="text-sm text-gray-500">{automation.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {automation.platforms.map(platform => (
              <Badge key={platform} variant="secondary">
                {platform}
              </Badge>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 space-y-8">
        {/* Goals Section */}
        <section>
          <h2 className="text-lg font-semibold mb-4">Set Your Goals</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <GoalSetting
              type="ENGAGEMENT"
              value={goals.engagement}
              onChange={(value) => handleGoalChange('engagement', value)}
            />
            <GoalSetting
              type="FREQUENCY"
              value={goals.frequency}
              onChange={(value) => handleGoalChange('frequency', value)}
            />
            <GoalSetting
              type="REACH"
              value={goals.reach}
              onChange={(value) => handleGoalChange('reach', value)}
            />
          </div>
        </section>

        {/* Video Slots Section */}
        {videoSlots.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Required Videos</h2>
              <Badge variant="outline" className="gap-1">
                <Video className="w-4 h-4" />
                {videoSlots.length} videos needed
              </Badge>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
              {videoSlots.map((video, index) => (
                <div className="w-full max-w-[120px]" key={index}>
                  <VideoSlot
                    video={video}
                    onClick={() => {
                      // Handle video selection
                      console.log('Select video for slot', index);
                    }}
                  />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </motion.div>
  );
};

export default AutomationDetail; 